import React, { useState, useEffect } from 'react'
import { useAuth } from '../../context/AuthContext'
import { navigate } from 'gatsby'

/**
 * AuthBlock component - A versatile authentication blocking overlay
 * Used to restrict access to content until a user is authenticated
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The protected content
 * @param {string} props.title - Title shown in the auth prompt (default: "Login Required")
 * @param {string} props.message - Message shown in the auth prompt
 * @param {string} props.type - Visual style of the block: "overlay" (full overlay), "inline" (inline block), "minimal" (simple message)
 * @param {boolean} props.allowAdminOverride - Whether to show the admin override button
 * @param {Function} props.onAuthenticated - Callback when user becomes authenticated
 */
export default function AuthBlock({ 
  children, 
  title = "Login Required",
  message = "You need to be logged in to access this content.",
  type = "overlay",
  allowAdminOverride = false,
  onAuthenticated = () => {},
  redirectToLogin = false,
}) {
  const { user, loading, signInWithMagicLink } = useAuth();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  // Load Geist Mono font when component mounts
  useEffect(() => {
    if (typeof document !== 'undefined') {
      // Add multiple sources for the Geist Mono font
      const fontCss = `
        @font-face {
          font-family: 'Geist Mono';
          src: url('https://fonts.cdnfonts.com/s/98372/GeistMono-Regular.woff2') format('woff2'),
               url('https://fonts.cdnfonts.com/s/98372/GeistMono-Regular.woff') format('woff');
          font-weight: 400;
          font-style: normal;
          font-display: block;
        }
      `;
      
      // Add the font-face declaration
      const fontStyle = document.createElement('style');
      fontStyle.textContent = fontCss;
      document.head.appendChild(fontStyle);
      
      // Add link to Geist Mono font
      const fontLink = document.createElement('link');
      fontLink.rel = 'stylesheet';
      fontLink.href = 'https://fonts.cdnfonts.com/css/geist-mono?v=2';
      document.head.appendChild(fontLink);
      
      // Add styles to ensure Geist Mono is applied to inputs and buttons
      const styleTag = document.createElement('style');
      styleTag.innerHTML = `
        /* CRITICAL: Force Geist Mono on button text */
        .auth-geist-mono-button * {
          font-family: 'Geist Mono', monospace !important;
          text-transform: uppercase !important;
          letter-spacing: 0.025em !important;
        }
        
        /* Force Geist Mono on button itself and all states */
        .auth-geist-mono-button {
          font-family: 'Geist Mono', monospace !important;
          text-transform: uppercase !important;
          letter-spacing: 0.025em !important;
        }
        
        /* Force Geist Mono on confirmation messages - V2 with extra specificity */
        .confirmation-message, 
        .confirmation-message *,
        div.confirmation-message,
        div.confirmation-message p,
        div.confirmation-message span,
        body .confirmation-message,
        body .confirmation-message p,
        body .confirmation-message span {
          font-family: 'Geist Mono', monospace !important;
          letter-spacing: 0.02em !important;
        }
        
        /* Global rule to catch all confirmation messages anywhere */
        [class*="success"], [class*="success"] * {
          font-family: 'Geist Mono', monospace !important;
        }
        
        /* Force Geist Mono on placeholder text */
        input::placeholder,
        input::-webkit-input-placeholder,
        input::-moz-placeholder,
        input:-ms-input-placeholder,
        input:-moz-placeholder {
          font-family: 'Geist Mono', monospace !important;
          color: #666 !important;
        }
        
        /* Make sure the actual input text also uses Geist Mono */
        input[type="email"], 
        input[type="text"], 
        input.auth-geist-mono, 
        .auth-geist-mono input {
          font-family: 'Geist Mono', monospace !important;
        }
      `;
      document.head.appendChild(styleTag);
      
      // Super aggressive font application
      const forceGeistMono = () => {
        document.querySelectorAll('.auth-geist-mono-button').forEach(button => {
          // Replace the button's inner HTML with a strongly styled span
          const buttonText = button.textContent.includes('...') ? '...' : 'LOG IN';
          button.innerHTML = '';
          
          const strongSpan = document.createElement('strong');
          strongSpan.style.cssText = 'font-family: "Geist Mono", monospace !important; text-transform: uppercase !important; letter-spacing: 0.025em !important;';
          strongSpan.textContent = buttonText;
          
          button.appendChild(strongSpan);
          
          // Also apply the style directly to the button
          button.style.fontFamily = '"Geist Mono", monospace !important';
          button.style.textTransform = 'uppercase !important';
        });
        
        // Apply Geist Mono to confirmation message elements
        document.querySelectorAll('.confirmation-message, .confirmation-message *').forEach(element => {
          element.style.cssText += 'font-family: "Geist Mono", monospace !important; letter-spacing: 0.02em !important;';
          
          // Replace the text with a strongly styled version if it's a paragraph
          if (element.tagName === 'P') {
            const originalText = element.textContent;
            element.innerHTML = '';
            
            const strongText = document.createElement('span');
            strongText.style.cssText = 'font-family: "Geist Mono", monospace !important;';
            strongText.textContent = originalText;
            
            element.appendChild(strongText);
          }
        });
        
        // Apply Geist Mono to email input and placeholder
        document.querySelectorAll('input[type="email"], input.auth-geist-mono').forEach(input => {
          input.style.fontFamily = '"Geist Mono", monospace !important';
          
          // Create a style element that targets this specific input's placeholder
          const inputId = input.id || `input-${Math.random().toString(36).substring(2, 9)}`;
          if (!input.id) input.id = inputId;
          
          const placeholderStyle = document.createElement('style');
          placeholderStyle.innerHTML = `
            #${inputId}::placeholder,
            #${inputId}::-webkit-input-placeholder,
            #${inputId}::-moz-placeholder,
            #${inputId}:-ms-input-placeholder,
            #${inputId}:-moz-placeholder {
              font-family: 'Geist Mono', monospace !important;
              color: #666 !important;
            }
          `;
          document.head.appendChild(placeholderStyle);
        });
      };
      
      // Run the font forcing multiple times
      setTimeout(forceGeistMono, 0);
      setTimeout(forceGeistMono, 100);
      setTimeout(forceGeistMono, 500);
      setTimeout(forceGeistMono, 1000); // Add another timeout for extra certainty
      
      // Clean up
      return () => {
        document.head.removeChild(styleTag);
        document.head.removeChild(fontLink);
        document.head.removeChild(fontStyle);
      };
    }
  }, []);

  // Force authentication (for admin/debugging)
  const forceAuthentication = () => {
    if (typeof window !== 'undefined') {
      localStorage.setItem("fishMapTempAccess", "true");
      window.location.reload();
    }
  };

  // Handle magic link submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (redirectToLogin) {
      // Redirect to login page using Gatsby's navigate
      navigate('/login');
      return;
    }
    setIsSubmitting(true);
    setError(null);

    try {
      const { error: signInError } = await signInWithMagicLink(email);
      if (signInError) throw signInError;
      setSubmitSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Apply direct custom placeholder
  useEffect(() => {
    if (typeof document !== 'undefined') {
      // Create a style element for direct Geist Mono application to placeholders
      const placeholderStyle = document.createElement('style');
      placeholderStyle.textContent = `
        /* Direct placeholder styling */
        input::placeholder,
        input::-webkit-input-placeholder {
          font-family: 'Geist Mono', monospace !important;
        }
      `;
      document.head.appendChild(placeholderStyle);
      
      // Return cleanup function
      return () => {
        document.head.removeChild(placeholderStyle);
      };
    }
  }, []);

  // Show loading state
  if (loading) {
    return children || <div className="loading-spinner">Loading...</div>;
  }

  // If user is authenticated, show the protected content
  if (user) {
    // Run the callback if provided
    onAuthenticated();
    return children || null;
  }

  // Check if manual override is active
  if (typeof window !== 'undefined' && localStorage.getItem("fishMapTempAccess") === "true") {
    return children || null;
  }

  // Full overlay block
  if (type === "overlay") {
    return (
      <div className="auth-overlay" style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 51,
        padding: "20px"
      }}>
        <div className="auth-content p-8 max-w-lg w-full text-center" style={{
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          borderRadius: "0.5rem",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          padding: "2rem"
        }}>
          <h2 className="text-2xl font-bold mb-4" style={{color: "white"}}>{title}</h2>
          <p className="mb-6" style={{color: "white"}}>{message}</p>
          
          <form onSubmit={handleSubmit} style={{ maxWidth: "400px", width: "100%", margin: "0 auto" }}>
            <div style={{ 
              position: "relative",
              width: "100%"
            }}>
              {redirectToLogin ? (
                <button
                  type="submit"
                  className="auth-geist-mono-button"
                  style={{
                    width: "100%",
                    padding: "0.75rem 1.25rem",
                    backgroundColor: "#1bb0b0",
                    color: "white",
                    border: "2px solid white",
                    borderRadius: "9999px",
                    fontSize: "1rem",
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                    fontFamily: '"Geist Mono", monospace !important',
                    fontWeight: "500",
                    letterSpacing: "0.025em",
                    textAlign: "center",
                    textTransform: "uppercase"
                  }}
                >
                  <span style={{ fontFamily: '"Geist Mono", monospace !important', textTransform: 'uppercase !important' }}>
                    LOG IN TO ACCESS
                  </span>
                </button>
              ) : (
                <>
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="auth-geist-mono"
                    style={{
                      width: "100%",
                      padding: "0.75rem 1.25rem",
                      paddingRight: "130px",
                      fontSize: "1rem",
                      border: "2px solid #e0e0e0",
                      borderRadius: "9999px",
                      outline: "none",
                      backgroundColor: "white",
                      fontFamily: "'Geist Mono', monospace !important",
                      color: "#333",
                      caretColor: "#333",
                      cursor: "text"
                    }}
                    required
                  />
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="auth-geist-mono-button"
                    style={{
                      position: "absolute",
                      right: "4px",
                      top: "4px",
                      bottom: "4px",
                      padding: "0.5rem 1.25rem",
                      backgroundColor: "#1bb0b0",
                      color: "white",
                      border: "2px solid white",
                      borderRadius: "9999px",
                      fontSize: "0.875rem",
                      cursor: isSubmitting ? "wait" : "pointer",
                      opacity: isSubmitting ? 0.7 : 1,
                      transition: "opacity 0.2s, background-color 0.2s",
                      fontFamily: '"Geist Mono", monospace !important',
                      fontWeight: "500",
                      letterSpacing: "0.025em",
                      width: "110px",
                      textAlign: "center",
                      textTransform: "uppercase"
                    }}
                  >
                    <span style={{ fontFamily: '"Geist Mono", monospace !important', textTransform: 'uppercase !important' }}>
                      {isSubmitting ? "..." : "LOG IN"}
                    </span>
                  </button>
                </>
              )}
            </div>
            {error && (
              <p style={{ 
                color: "#dc3545", 
                fontSize: "0.875rem", 
                marginTop: "0.75rem",
                textAlign: "center"
              }}>
                {error}
              </p>
            )}
            {submitSuccess && (
              <div style={{ 
                marginTop: "0.75rem",
                width: "100%",
                textAlign: "center",
                color: "white",
                position: "absolute",
                left: 0,
                right: 0,
                fontFamily: '"Geist Mono", monospace !important'
              }} className="confirmation-message">
                <p style={{ fontFamily: '"Geist Mono", monospace !important' }}>Got it! Now check your email.</p>
              </div>
            )}
          </form>
          
          {allowAdminOverride && (
            <div className="mt-8 pt-4">
              <button 
                onClick={forceAuthentication}
                className="text-sm text-gray-300 hover:text-white"
              >
                Admin: Override Access Check
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }
  
  // Inline block (for charts, widgets, etc.)
  if (type === "inline") {
    return (
      <div className="auth-inline-block" style={{
        position: "relative",
        borderRadius: "0.5rem",
        overflow: "hidden",
        border: "1px solid #e0e0e0",
        minHeight: "200px"
      }}>
        {/* Show a blurred preview of the content */}
        <div style={{
          filter: "blur(8px)",
          opacity: 0.4,
          pointerEvents: "none",
          userSelect: "none"
        }}>
          {children}
        </div>
        
        {/* Auth overlay */}
        <div style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "1.5rem",
          textAlign: "center"
        }}>
          <h3 style={{
            fontSize: "1.25rem",
            fontWeight: "bold",
            marginBottom: "0.75rem"
          }}>{title}</h3>
          <p style={{
            marginBottom: "1.5rem",
            fontSize: "0.875rem"
          }}>{message}</p>
          
          <form onSubmit={handleSubmit} style={{ width: "100%", maxWidth: "300px" }}>
            <div style={{ 
              position: "relative",
              width: "100%",
              marginBottom: "0.75rem"
            }}>
              <input
                type="email"
                name="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="auth-geist-mono"
                style={{
                  width: "100%",
                  padding: "0.625rem 1rem",
                  fontSize: "0.875rem",
                  border: "1px solid #e0e0e0",
                  borderRadius: "0.375rem",
                  outline: "none",
                  backgroundColor: "white",
                  fontFamily: "'Geist Mono', monospace !important",
                  color: "#333"
                }}
                required
              />
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="auth-geist-mono-button"
              style={{
                width: "100%",
                padding: "0.625rem 1rem",
                backgroundColor: "#1bb0b0",
                color: "white",
                border: "none",
                borderRadius: "0.375rem",
                fontSize: "0.875rem",
                cursor: isSubmitting ? "wait" : "pointer",
                opacity: isSubmitting ? 0.7 : 1,
                transition: "all 0.2s",
                fontFamily: '"Geist Mono", monospace !important',
                fontWeight: "500",
                textTransform: "uppercase"
              }}
            >
              <span style={{ fontFamily: '"Geist Mono", monospace !important', textTransform: 'uppercase !important' }}>
                {isSubmitting ? "..." : "LOG IN"}
              </span>
            </button>
            
            {error && (
              <p style={{ 
                color: "#dc3545", 
                fontSize: "0.75rem", 
                marginTop: "0.5rem",
                textAlign: "center"
              }}>
                {error}
              </p>
            )}
            {submitSuccess && (
              <div style={{ 
                marginTop: "0.75rem",
                width: "100%",
                textAlign: "center",
                color: "#1bb0b0",
                fontSize: "0.875rem",
                position: "absolute",
                left: 0,
                right: 0,
                fontFamily: '"Geist Mono", monospace !important'
              }} className="confirmation-message">
                <p style={{ fontFamily: '"Geist Mono", monospace !important' }}>Got it! Now check your email.</p>
              </div>
            )}
          </form>
          
          {allowAdminOverride && (
            <button 
              onClick={forceAuthentication}
              style={{
                marginTop: "1rem",
                fontSize: "0.75rem",
                color: "#888",
                background: "none",
                border: "none",
                cursor: "pointer"
              }}
            >
              Admin: Override Access Check
            </button>
          )}
        </div>
      </div>
    );
  }
  
  // Minimal block (just a message and link)
  if (type === "minimal") {
    return (
      <div className="auth-minimal" style={{
        padding: "1rem",
        backgroundColor: "#f9f9f9",
        borderRadius: "0.375rem",
        marginBottom: "1rem",
        textAlign: "center"
      }}>
        <p style={{ marginBottom: "0.75rem" }}>{message}</p>
        <button 
          onClick={() => window.location.href = '/login'}
          className="auth-geist-mono-button"
          style={{
            padding: "0.375rem 0.75rem",
            backgroundColor: "#1bb0b0",
            color: "white",
            border: "none",
            borderRadius: "0.25rem",
            cursor: "pointer",
            fontFamily: '"Geist Mono", monospace !important',
            textTransform: "uppercase"
          }}
        >
          LOG IN
        </button>
      </div>
    );
  }
  
  return null;
} 